import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Printer, RotateCcw } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CHECKLIST_PHASES, TOTAL_TASKS, type ChecklistPhase } from "@/content/checklist";
import { GUIDES } from "@/content/guides";
import { checklistCopy, guidesCopy } from "@/content/pages";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  detectLocale,
  getTranslations,
  type Locale,
} from "@/i18n/translations";

const BASE_URL = "https://nellove.com";
const STORAGE_KEY = "nellove_checklist_v1";

const PATHS: Record<Locale, string> = {
  en: "/en/wedding-planning-checklist",
  fr: "/fr/checklist-mariage",
};

type StoredState = { date: string; checked: string[] };

const parseDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const addMonths = (date: Date, months: number) => {
  const d = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(date.getDate(), lastDay));
  return d;
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const phaseDate = (phase: ChecklistPhase, wedding: Date) => {
  if (phase.after) return addDays(wedding, 14);
  if (phase.daysBefore) return addDays(wedding, -phase.daysBefore);
  return addMonths(wedding, -(phase.monthsBefore ?? 0));
};

const Checklist = ({ forcedLocale }: { forcedLocale?: Locale }) => {
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = (forcedLocale ??
    (SUPPORTED_LOCALES.includes(locale as Locale) ? locale : DEFAULT_LOCALE)) as Locale;
  const t = getTranslations(currentLocale);
  const copy = checklistCopy[currentLocale];
  const gCopy = guidesCopy[currentLocale];

  const [date, setDate] = useState("");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.documentElement.lang = currentLocale;
  }, [currentLocale]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as StoredState;
        if (typeof parsed.date === "string") setDate(parsed.date);
        if (Array.isArray(parsed.checked)) setChecked(new Set(parsed.checked));
      }
    } catch {
      /* ignore unreadable storage */
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ date, checked: [...checked] }));
    } catch {
      /* storage may be unavailable */
    }
  }, [date, checked, loaded]);

  const wedding = parseDate(date);
  const today = startOfToday();
  const isPast = !!wedding && wedding < today;

  const groups = useMemo(() => {
    if (!wedding || isPast) return null;
    const upcoming: { phase: ChecklistPhase; date: Date }[] = [];
    const overdue: ChecklistPhase[] = [];

    CHECKLIST_PHASES.forEach((phase) => {
      const d = phaseDate(phase, wedding);
      if (!phase.after && d < today) overdue.push(phase);
      else upcoming.push({ phase, date: d });
    });

    return { overdue, upcoming };
  }, [wedding, isPast, today]);

  const formatDate = (d: Date) =>
    d.toLocaleDateString(currentLocale === "fr" ? "fr-CA" : "en-CA", {
      month: "long",
      year: "numeric",
    });

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doneCount = CHECKLIST_PHASES.reduce(
    (n, p) => n + p.tasks.filter((task) => checked.has(task.id)).length,
    0,
  );

  const canonical = `${BASE_URL}${PATHS[currentLocale]}`;

  const renderTask = (task: ChecklistPhase["tasks"][number]) => {
    const id = `task-${task.id}`;
    const isChecked = checked.has(task.id);
    return (
      <li key={task.id} className="flex items-start gap-3 border-b border-stone-200 py-3 last:border-b-0">
        <Checkbox
          id={id}
          checked={isChecked}
          onCheckedChange={() => toggle(task.id)}
          className="mt-1 shrink-0"
        />
        <label htmlFor={id} className="cursor-pointer">
          <span className={`text-[16px] leading-snug ${isChecked ? "text-stone-600 line-through" : "text-foreground"}`}>
            {task.label[currentLocale]}
          </span>
          {task.detail && (
            <span className="mt-1 block text-[14px] leading-relaxed text-stone-600">
              {task.detail[currentLocale]}
            </span>
          )}
        </label>
      </li>
    );
  };

  const phaseCard = (title: string, summary: string, when: string | null, phases: ChecklistPhase[]) => (
    <section key={title} className="rounded-xl border border-stone-200 bg-card p-5 shadow-soft sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-serif text-[22px] tracking-tight">{title}</h2>
        {when && (
          <span className="text-[13px] text-stone-600">
            {copy.dateColumn} {when}
          </span>
        )}
      </div>
      <p className="mt-1 text-[14px] leading-relaxed text-stone-600">{summary}</p>
      <ul className="mt-4">{phases.flatMap((p) => p.tasks).map(renderTask)}</ul>
    </section>
  );

  return (
    <SiteLayout locale={currentLocale} t={t} altPath={PATHS[currentLocale === "en" ? "fr" : "en"]}>
      <Helmet>
        <html lang={currentLocale} />
        <title>{copy.metaTitle}</title>
        <meta name="description" content={copy.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${BASE_URL}${PATHS.en}`} />
        <link rel="alternate" hrefLang="fr" href={`${BASE_URL}${PATHS.fr}`} />
        <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${PATHS.en}`} />
        <meta property="og:title" content={copy.metaTitle} />
        <meta property="og:description" content={copy.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <meta property="og:site_name" content="Nellove" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={copy.metaTitle} />
        <meta name="twitter:description" content={copy.metaDescription} />
        <meta name="twitter:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "HowTo",
                name: copy.heading,
                description: copy.metaDescription,
                inLanguage: currentLocale === "fr" ? "fr-CA" : "en-CA",
                totalTime: "P12M",
                step: CHECKLIST_PHASES.map((phase, i) => ({
                  "@type": "HowToSection",
                  position: i + 1,
                  name: phase.label[currentLocale],
                  itemListElement: phase.tasks.map((task, j) => ({
                    "@type": "HowToStep",
                    position: j + 1,
                    name: task.label[currentLocale],
                    text: task.detail?.[currentLocale] ?? task.label[currentLocale],
                  })),
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: gCopy.breadcrumbHome,
                    item: `${BASE_URL}/${currentLocale}`,
                  },
                  { "@type": "ListItem", position: 2, name: copy.heading, item: canonical },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <section className="border-b border-stone-200 bg-secondary px-5 py-14 sm:px-6 md:py-20">
        <div className="mx-auto max-w-3xl">
          <p className="inline-flex items-center rounded-full border border-rust-400 bg-rust-50 px-3.5 py-1.5 text-[13px] font-medium tracking-[0.08em] text-rust-600">
            {copy.eyebrow}
          </p>
          <h1 className="mt-5 text-[36px] leading-[1.08] tracking-tight sm:text-[46px]">{copy.heading}</h1>
          <p className="mt-5 text-[17px] leading-relaxed text-stone-700">{copy.intro}</p>

          <div className="mt-8 rounded-xl border border-stone-200 bg-card p-5 shadow-soft sm:p-6">
            <label htmlFor="wedding-date" className="block text-[15px] font-medium text-stone-700">
              {copy.dateLabel}
            </label>
            <input
              id="wedding-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-describedby="wedding-date-hint"
              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-[16px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust-500"
            />
            <p id="wedding-date-hint" className="mt-2 text-[13px] text-stone-600">
              {copy.dateHint}
            </p>
            {isPast && (
              <p role="alert" className="mt-3 text-[14px] font-medium text-destructive">
                {copy.pastDate}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="px-5 py-14 sm:px-6 md:py-16">
        <div className="mx-auto max-w-3xl">
          {!groups ? (
            <div className="rounded-xl border border-stone-200 bg-card p-6 shadow-soft">
              <h2 className="font-serif text-[24px] tracking-tight">{copy.emptyTitle}</h2>
              <p className="mt-2 text-[16px] leading-relaxed text-stone-600">{copy.emptyBody}</p>
            </div>
          ) : (
            <>
              <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[15px] font-medium text-stone-700" role="status" aria-live="polite">
                  {copy.progress(doneCount, TOTAL_TASKS)}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.print()}
                    className="h-11 gap-2 rounded-xl border-rust-400 text-rust-600"
                  >
                    <Printer className="h-4 w-4" aria-hidden="true" />
                    {copy.print}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setChecked(new Set())}
                    className="h-11 gap-2 rounded-xl text-stone-700"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    {copy.reset}
                  </Button>
                </div>
              </div>

              <div className="space-y-6">
                {groups.overdue.length > 0 &&
                  phaseCard(copy.startNow, copy.startNowSummary, null, groups.overdue)}
                {groups.upcoming.map(({ phase, date: d }) =>
                  phaseCard(
                    phase.label[currentLocale],
                    phase.summary[currentLocale],
                    formatDate(d),
                    [phase],
                  ),
                )}
              </div>

              <p className="mt-6 text-[13px] text-stone-600">{copy.savedNote}</p>
            </>
          )}

          <div className="mt-12 rounded-xl border border-stone-200 bg-secondary p-6">
            <h2 className="font-serif text-[22px] tracking-tight">{copy.howItWorksHeading}</h2>
            <ul className="mt-3 space-y-2">
              {copy.howItWorks.map((line) => (
                <li key={line} className="flex gap-2 text-[15px] leading-relaxed text-stone-700">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-rust-600" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <h2 className="mt-6 font-serif text-[22px] tracking-tight">{copy.accuracyHeading}</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-stone-700">{copy.accuracyBody}</p>
          </div>

          <div className="no-print mt-12 rounded-xl border border-stone-200 bg-card p-6 shadow-card">
            <h2 className="font-serif text-[26px] tracking-tight">{copy.ctaHeading}</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-stone-600">{copy.ctaBody}</p>
            <a
              href={`/${currentLocale}#join`}
              className="mt-5 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-200 hover:bg-rust-600"
            >
              {copy.ctaButton}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          <div className="no-print mt-12">
            <h2 className="font-serif text-[22px] tracking-tight">{copy.guidesHeading}</h2>
            <ul className="mt-4 space-y-3">
              {GUIDES.map((guide) => (
                <li key={guide.id}>
                  <a
                    href={`/${currentLocale}/guides/${guide.content[currentLocale].slug}`}
                    className="text-[16px] text-rust-600 underline-offset-4 hover:underline"
                  >
                    {guide.content[currentLocale].title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Checklist;
